import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, ArrowLeft, Loader2, Wand2, Search } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";

const empty = {
  slug: "", title: "", car_slug_a: "", car_slug_b: "",
  intro: "", verdict: "", category_winners: {}, pros_cons: {},
  hero_image: "", status: "draft",
  meta_title: "", meta_description: "", canonical_url: "", og_image: "",
};

export default function ComparisonsAdmin() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cars, setCars] = useState([]);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setBusy(true);
    api.get("/admin/comparisons").then((r) => setRows(r.data)).finally(() => setBusy(false));
    api.get("/cars?limit=200").then((r) => setCars(r.data));
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!editing?.car_slug_a || !editing?.car_slug_b) return toast.error("Pick both cars first");
    if (editing.car_slug_a === editing.car_slug_b) return toast.error("Pick two different cars");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/comparisons/generate", {
        car_slug_a: editing.car_slug_a, car_slug_b: editing.car_slug_b,
      });
      // Merge the AI-drafted fields into the current form (keep any manual edits already present)
      setEditing((cur) => ({ ...data, ...(cur?._existing ? cur : {}), ...cur, ...data,
        // But let AI-generated content override empty fields only:
        verdict: cur?.verdict || data.verdict,
        intro: cur?.intro || data.intro,
        title: cur?.title || data.title,
        slug: cur?.slug || data.slug,
        meta_title: cur?.meta_title || data.meta_title,
        meta_description: cur?.meta_description || data.meta_description,
        canonical_url: cur?.canonical_url || data.canonical_url,
        hero_image: cur?.hero_image || data.hero_image,
        og_image: cur?.og_image || data.og_image,
        category_winners: data.category_winners || {},
        pros_cons: data.pros_cons || {},
        car_slug_a: cur.car_slug_a, car_slug_b: cur.car_slug_b,
      }));
      toast.success("AI-generated draft ready \u2014 review and publish");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.slug || !editing.title || !editing.car_slug_a || !editing.car_slug_b)
      return toast.error("slug, title and both car slugs are required");
    try {
      if (editing._existing) await api.put(`/admin/comparisons/${editing.slug}`, editing);
      else await api.post("/admin/comparisons", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete comparison "${slug}"?`)) return;
    await api.delete(`/admin/comparisons/${slug}`);
    load();
  };

  if (editing) {
    return (
      <div data-testid="admin-comparison-edit">
        <button onClick={() => setEditing(null)} className="text-sm text-blue-600 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4"/> Back to comparisons
        </button>
        <h1 className="font-display text-3xl tracking-tighter">{editing._existing ? "Edit comparison" : "New comparison"}</h1>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Car A slug *">
                <CarSelect testId="comp-car-a" cars={cars} value={editing.car_slug_a}
                  onChange={(v) => setEditing({ ...editing, car_slug_a: v })}/>
              </Field>
              <Field label="Car B slug *">
                <CarSelect testId="comp-car-b" cars={cars} value={editing.car_slug_b}
                  onChange={(v) => setEditing({ ...editing, car_slug_b: v })}/>
              </Field>
            </div>
            <button type="button" onClick={generate} disabled={generating || !editing.car_slug_a || !editing.car_slug_b}
              data-testid="comp-generate"
              className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2 disabled:opacity-50">
              {generating ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
              {generating ? "Generating..." : "Generate with AI"}
            </button>
            <Field label="Title *">
              <input data-testid="comp-title" value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inp}/>
            </Field>
            <Field label="Slug *">
              <input data-testid="comp-slug" value={editing.slug} disabled={editing._existing}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                className={inp + (editing._existing ? " opacity-60" : "")}/>
            </Field>
            <Field label="Intro">
              <textarea data-testid="comp-intro" value={editing.intro || ""}
                onChange={(e) => setEditing({ ...editing, intro: e.target.value })}
                className={`${inp} min-h-[80px] py-2`}/>
            </Field>
            <Field label="Verdict *">
              <textarea data-testid="comp-verdict" value={editing.verdict}
                onChange={(e) => setEditing({ ...editing, verdict: e.target.value })}
                className={`${inp} min-h-[120px] py-2`}/>
            </Field>
            {Object.keys(editing.category_winners || {}).length > 0 && (
              <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Category winners</div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  {Object.entries(editing.category_winners).map(([cat, win]) => (
                    <div key={cat} className="flex justify-between p-2 rounded-lg bg-[#F8FAFC]">
                      <span className="text-muted-foreground">{cat}</span>
                      <span className="font-medium">{win}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <Field label="Hero image">
              <ImageUpload value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v })} folder="comparisons" testId="comp-hero"/>
            </Field>
            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Publish</div>
              <Field label="Status">
                <select data-testid="comp-status" value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inp}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </Field>
            </div>
            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">SEO</div>
              <Field label="Meta title">
                <input data-testid="comp-meta-title" value={editing.meta_title || ""}
                  onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} className={inp}/>
              </Field>
              <Field label="Meta description">
                <textarea data-testid="comp-meta-desc" value={editing.meta_description || ""}
                  onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })}
                  className={`${inp} min-h-[70px] py-2`}/>
              </Field>
              <Field label="Canonical URL">
                <input data-testid="comp-canonical" value={editing.canonical_url || ""}
                  onChange={(e) => setEditing({ ...editing, canonical_url: e.target.value })} className={inp}/>
              </Field>
            </div>
            <button onClick={save} data-testid="comp-save" className="btn-primary w-full h-11 text-sm inline-flex items-center justify-center gap-2">
              <Save className="w-4 h-4"/> Save comparison
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-comparisons">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Comparisons</h1>
          <p className="text-sm text-muted-foreground mt-1">Generate SEO-friendly side-by-side comparison pages between any two cars.</p>
        </div>
        <button data-testid="comp-new" onClick={() => setEditing({ ...empty })} className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> New comparison
        </button>
      </div>
      <div className="mt-6 rounded-3xl border border-[#E5E7EB] overflow-hidden bg-white">
        {busy ? (
          <div className="grid place-items-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">No comparisons yet. Create your first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Title</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Cars</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</th>
                <th className="p-3"/>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} data-testid={`comp-row-${r.slug}`} className="border-t border-[#E5E7EB]">
                  <td className="p-3">
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-muted-foreground">{r.slug}</div>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{r.car_slug_a} vs {r.car_slug_b}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${r.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>{r.status}</span>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing({ ...r, _existing: true })} data-testid={`comp-edit-${r.slug}`} className="text-sm text-blue-600 hover:underline mr-3">Edit</button>
                    <button onClick={() => del(r.slug)} data-testid={`comp-del-${r.slug}`} className="text-sm text-rose-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CarSelect({ cars, value, onChange, testId }) {
  const [q, setQ] = useState(value || "");
  useEffect(() => { setQ(value || ""); }, [value]);
  const opts = q
    ? cars.filter((c) => `${c.brand_name} ${c.model} ${c.slug}`.toLowerCase().includes(q.toLowerCase())).slice(0, 20)
    : cars.slice(0, 20);
  return (
    <div className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
      <input data-testid={testId} value={q} onChange={(e) => { setQ(e.target.value); onChange(e.target.value); }}
        placeholder="Search a car by brand/model" list={`${testId}-list`}
        className={`${inp} pl-9`}/>
      <datalist id={`${testId}-list`}>
        {opts.map((c) => <option key={c.slug} value={c.slug}>{c.brand_name} {c.model}</option>)}
      </datalist>
    </div>
  );
}

const inp = "w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
